import type { ConfidenceBreakdownDto } from './ConfidenceBreakdown';
import type { AssociatedBusinessDto } from './MentionReview';
import { parseAsUTC } from '@/infrastructure/utils/dateUtils';

/**
 * PostReview - Frontend types for post-level review feature
 *
 * Maps to Backend DTOs from:
 * - Backend/MentionedMe.Application/Features/PostReview/DTOs/PostSummaryDto.cs
 * - Backend/MentionedMe.Application/Features/PostReview/DTOs/PostMentionGroupDto.cs
 * - Backend/MentionedMe.Application/Features/PostReview/DTOs/CommentMentionGroupDto.cs
 * - Backend/MentionedMe.Application/Features/PostReview/DTOs/MentionDto.cs
 *
 * Type Mapping (C# → TypeScript):
 * - Guid → string
 * - DateTime → string (ISO 8601)
 * - int → number
 * - decimal → number
 * - bool → boolean
 * - string? → string | null
 */

/**
 * Post summary with aggregated mention statistics for post-level review list view
 *
 * Backend: PostSummaryDto.cs
 */
export interface PostSummaryDto {
  // Post metadata
  redditPostId: string;
  postTitle: string;
  subreddit: string;
  postCreatedAt: string; // DateTime → ISO 8601 string
  postPermalink: string;
  authorUsername: string;
  upvoteCount: number;
  commentCount: number;

  // Aggregated mention statistics
  totalMentionCount: number;
  highConfidenceCount: number; // >= 90%
  mediumConfidenceCount: number; // 70-89%
  lowConfidenceCount: number; // < 70%
  pendingCount: number;
  approvedCount: number;
  rejectedCount: number;
}

/**
 * Complete hierarchical structure of a post with all its mentions for post-level review
 * Hierarchy: Post → Top-level mentions + Comments → Comment mentions
 *
 * Backend: PostMentionGroupDto.cs
 */
export interface PostMentionGroupDto {
  // Post metadata (inherited from PostSummaryDto)
  redditPostId: string;
  postTitle: string;
  subreddit: string;
  postCreatedAt: string;
  postPermalink: string;
  authorUsername: string;
  upvoteCount: number;
  commentCount: number;
  content: string; // Full post content

  // Aggregated mention statistics (same as PostSummaryDto)
  totalMentionCount: number;
  highConfidenceCount: number;
  mediumConfidenceCount: number;
  lowConfidenceCount: number;
  pendingCount: number;
  approvedCount: number;
  rejectedCount: number;

  // Hierarchical mention structure
  topLevelMentions: MentionDto[]; // Mentions in the post itself
  comments: CommentMentionGroupDto[]; // Comments with their mentions
}

/**
 * Groups mentions within a comment for hierarchical post-level review
 *
 * Backend: CommentMentionGroupDto.cs
 */
export interface CommentMentionGroupDto {
  // Comment metadata
  redditCommentId: string | null;
  authorUsername: string;
  content: string;
  createdAt: string; // DateTime → ISO 8601 string
  upvoteCount: number;

  // Mentions found within this comment
  mentions: MentionDto[];
}

/**
 * Enhanced mention DTO for post-level review with company/location metadata
 *
 * Backend: MentionDto.cs
 */
export interface MentionDto {
  id: string; // Guid → string
  content: string;
  mentionText: string;
  matchConfidenceScore: number; // 0-100
  reviewStatus: string; // Pending, Approved, Rejected
  createdAt: string; // DateTime → ISO 8601 string
  reviewedAt: string | null; // DateTime? → ISO 8601 string | null
  adminNotes: string | null;

  // Comment metadata (if this mention is from a comment)
  isComment: boolean;
  parentPostId: string | null;

  // Reddit link construction (SESSION_053)
  redditPostId: string;
  subreddit: string;

  // Region (for business reassignment/creation)
  regionId: string | null;

  // Matched business information (may be null if unmatched or low confidence)
  // LEGACY: Single business fields maintained for backward compatibility
  businessId: string | null; // Guid? → string | null
  businessName: string | null;
  businessAddress: string | null;
  businessPhone: string | null;

  // Multi-business associations (Sprint 014 Phase 8 - SESSION_121)
  /**
   * All businesses associated with this mention (many-to-many relationship)
   */
  associatedBusinesses: AssociatedBusinessDto[];

  // Company/franchise handling (SESSION_046)
  isCompanyMention: boolean;
  locationSpecificity: string | null; // AllLocations, Specific, Unknown
  specificBusinessLocationId: number | null; // int? → number | null

  // Training data indicators
  wasMatchCorrected: boolean;
  originalMatchedBusinessId: string | null; // Guid? → string | null

  // Confidence score breakdown
  confidenceBreakdown: ConfidenceBreakdownDto | null;
}

/**
 * Query parameters for fetching posts with mentions
 *
 * Backend: PostReviewController.cs GetPostsWithMentions parameters
 */
export interface GetPostsForReviewParams {
  /**
   * Page number (1-indexed, default: 1)
   */
  page?: number;

  /**
   * Items per page (default: 20)
   */
  pageSize?: number;

  /**
   * Filter by subreddit name (e.g., "Seattle", "AskSeattle")
   */
  subreddit?: string | null;

  /**
   * Filter by review status
   * - "AllPending" - All mentions pending
   * - "SomeReviewed" - Mixed reviewed and pending
   * - "AllReviewed" - All mentions reviewed
   */
  reviewStatus?: string | null;

  /**
   * Minimum number of mentions to include post
   */
  minMentionCount?: number | null;

  /**
   * Filter posts created after this date (ISO 8601)
   */
  startDate?: string | null;

  /**
   * Filter posts created before this date (ISO 8601)
   */
  endDate?: string | null;

  /**
   * Filter by algorithm version (e.g., "1.0.0", "1.0.1")
   * Sprint 038: Enables investigation of matches created under specific algorithm versions
   */
  algorithmVersion?: string | null;

  /**
   * Sort field (default: "PostDate")
   * - "PostDate" - Post creation date
   * - "MentionCount" - Total mention count
   * - "HighConfidenceCount" - High confidence mention count
   */
  sortBy?: string;

  /**
   * Sort direction (default: "Desc")
   * - "Asc" - Ascending
   * - "Desc" - Descending
   */
  sortDirection?: string;
}

/**
 * Review status filter options
 */
export const REVIEW_STATUS_FILTERS = {
  ALL_PENDING: 'AllPending',
  SOME_REVIEWED: 'SomeReviewed',
  ALL_REVIEWED: 'AllReviewed'
} as const;

/**
 * Sort field options
 */
export const SORT_BY_OPTIONS = {
  POST_DATE: 'PostDate',
  MENTION_COUNT: 'MentionCount',
  HIGH_CONFIDENCE_COUNT: 'HighConfidenceCount'
} as const;

/**
 * Sort direction options
 */
export const SORT_DIRECTION_OPTIONS = {
  ASC: 'Asc',
  DESC: 'Desc'
} as const;

/**
 * Location specificity options for company mentions
 */
export const LOCATION_SPECIFICITY = {
  ALL_LOCATIONS: 'AllLocations',
  SPECIFIC: 'Specific',
  UNKNOWN: 'Unknown'
} as const;

/**
 * Helper: Get confidence level category from score
 */
export function getConfidenceLevel(score: number): 'high' | 'medium' | 'low' {
  if (score >= 90) return 'high';
  if (score >= 70) return 'medium';
  return 'low';
}

/**
 * Helper: Get confidence badge color (Mantine color)
 */
export function getConfidenceBadgeColor(score: number): string {
  const level = getConfidenceLevel(score);
  return {
    high: 'green',
    medium: 'blue',
    low: 'yellow'
  }[level];
}

/**
 * Helper: Format confidence score for display
 */
export function formatConfidenceScore(score: number): string {
  return `${score}%`;
}

/**
 * Helper: Get review status badge color (Mantine color)
 */
export function getReviewStatusColor(status: string): string {
  const statusMap: Record<string, string> = {
    Pending: 'yellow',
    Approved: 'green',
    Rejected: 'red',
    Flagged: 'orange'
  };
  return statusMap[status] || 'gray';
}

/**
 * Helper: Format date for display (relative or absolute)
 */
export function formatPostDate(dateString: string): string {
  const date = parseAsUTC(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffHours < 1) {
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
  }
  if (diffHours < 24) {
    return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  }
  if (diffDays < 7) {
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  }

  return date.toLocaleDateString();
}

/**
 * Helper: Calculate overall review progress
 */
export function calculateReviewProgress(post: PostSummaryDto): number {
  if (post.totalMentionCount === 0) return 0;
  const reviewedCount = post.approvedCount + post.rejectedCount;
  return Math.round((reviewedCount / post.totalMentionCount) * 100);
}

/**
 * Helper: Determine if post is fully reviewed
 */
export function isPostFullyReviewed(post: PostSummaryDto): boolean {
  return post.pendingCount === 0 && post.totalMentionCount > 0;
}

/**
 * Helper: Get human-readable location specificity label
 */
export function getLocationSpecificityLabel(specificity: string | null): string {
  if (!specificity) return 'Unknown';

  const labels: Record<string, string> = {
    [LOCATION_SPECIFICITY.ALL_LOCATIONS]: 'All Locations',
    [LOCATION_SPECIFICITY.SPECIFIC]: 'Specific Location',
    [LOCATION_SPECIFICITY.UNKNOWN]: 'Unknown'
  };

  return labels[specificity] || specificity;
}
